// Phase NA1-3/NA1-4 — NAATI Document Matrix (renders inside CertTypeHub for NAATI only)
// Shows all 28 document types, filterable by category, with honest pricing/turnaround.
import { useState } from "react";
import { NAATI_DOCTYPES, NAATI_DOCTYPE_CATEGORIES, NAATI_DOCTYPE_STATS, type NaatiDocCategory } from "@/data/naati-doctypes";
import { FileText, Filter } from "lucide-react";

export default function NaatiDoctypeMatrix({ lang = "th" }: { lang?: "th" | "en" }) {
  const isTH = lang === "th";
  const [active, setActive] = useState<NaatiDocCategory | "all">("all");
  const rows = active === "all" ? NAATI_DOCTYPES : NAATI_DOCTYPES.filter((d) => d.category === active);

  const L = isTH
    ? {
        title: `ตารางเอกสารแปล NAATI (${NAATI_DOCTYPE_STATS.count} ชนิด)`,
        subtitle: `ราคาเริ่ม ฿${NAATI_DOCTYPE_STATS.priceMin}-${NAATI_DOCTYPE_STATS.priceMax}/หน้า A4 รวมตรา NAATI + CPN — กรองตามหมวด`,
        all: "ทั้งหมด",
        doc: "ชนิดเอกสาร",
        price: "เริ่มต้น (THB/หน้า)",
        time: "ระยะเวลา",
        pages: "จำนวนหน้าทั่วไป",
        notes: "ข้อสังเกต",
        usedBy: "ใช้กับ",
      }
    : {
        title: `NAATI Document Catalog (${NAATI_DOCTYPE_STATS.count} types)`,
        subtitle: `THB ${NAATI_DOCTYPE_STATS.priceMin}-${NAATI_DOCTYPE_STATS.priceMax}/A4 page including NAATI stamp + CPN — filter by category`,
        all: "All",
        doc: "Document type",
        price: "From (THB/page)",
        time: "Turnaround",
        pages: "Typical pages",
        notes: "Notes",
        usedBy: "Used for",
      };

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-16"
      aria-label={L.title}
      data-track-zone="naati-doctype-matrix"
    >
      <header data-speakable>
        <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">
          <FileText className="mr-2 inline-block h-6 w-6 text-amber-600" />
          {L.title}
        </h2>
        <p className="mt-2 text-sm text-slate-600">{L.subtitle}</p>
      </header>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-slate-500" />
        <button
          onClick={() => setActive("all")}
          className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
            active === "all" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-600 hover:border-amber-300"
          }`}
        >
          {L.all} ({NAATI_DOCTYPES.length})
        </button>
        {NAATI_DOCTYPE_CATEGORIES.map((c) => {
          const count = NAATI_DOCTYPES.filter((d) => d.category === c.id).length;
          return (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              className={`rounded-full border px-3 py-1 text-xs font-bold transition ${
                active === c.id ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 bg-white text-slate-600 hover:border-amber-300"
              }`}
            >
              {isTH ? c.nameTh : c.nameEn} ({count})
            </button>
          );
        })}
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-slate-200">
        <table className="w-full min-w-[720px] text-left text-[13px]">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
            <tr>
              <th className="px-4 py-3">{L.doc}</th>
              <th className="px-4 py-3 text-right">{L.price}</th>
              <th className="px-4 py-3">{L.time}</th>
              <th className="px-4 py-3">{L.pages}</th>
              <th className="px-4 py-3">{L.notes}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((d) => (
              <tr key={d.slug} className="hover:bg-amber-50/30">
                <td className="px-4 py-3">
                  <div className="font-bold text-navy-deep">{isTH ? d.nameTh : d.nameEn}</div>
                  <div className="mt-0.5 text-[10px] text-slate-500">
                    {L.usedBy}: {d.useCases.slice(0, 2).join(" · ")}
                  </div>
                </td>
                <td className="px-4 py-3 text-right font-mono font-bold text-amber-700">
                  ฿{d.priceFromThb.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-slate-600">{d.turnaround}</td>
                <td className="px-4 py-3 text-slate-600">{d.pagesTypical}</td>
                <td className="px-4 py-3 text-[12px] text-slate-600">
                  {isTH ? d.notesTh : d.notesEn}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-3 text-[11px] text-slate-500">
        {isTH
          ? "หมายเหตุ: Express 24 ชั่วโมง +50% / ปริมาณ 5+ หน้า ส่วนลด 10-20% / รวมตรา NAATI + CPN + ไฟล์ PDF ทุกราคา"
          : "Notes: Express 24h +50% / 5+ pages volume discount 10-20% / NAATI stamp + CPN + PDF copy included in all prices"}
      </p>
    </section>
  );
}
